import PropTypes from 'prop-types'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import {
  detectDefaultUnitSystem,
  distanceInUnits,
  distanceParts,
  distanceUnit,
  formatDistance,
  formatSpeed,
  UNIT_SYSTEMS,
} from '@/utils/units'

const STORAGE_KEY = 'unitSystem'

const isUnitSystem = (value) => Object.values(UNIT_SYSTEMS).includes(value)

/** The saved preference, else the system the browser's locale implies */
function initialUnitSystem() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (isUnitSystem(saved)) return saved
  } catch {
    // localStorage unavailable; fall through to detection
  }
  return detectDefaultUnitSystem()
}

function buildValue(unitSystem, setUnitSystem) {
  return {
    unitSystem,
    isImperial: unitSystem === UNIT_SYSTEMS.IMPERIAL,
    setUnitSystem,
    toggleUnitSystem: () =>
      setUnitSystem(
        unitSystem === UNIT_SYSTEMS.IMPERIAL ? UNIT_SYSTEMS.METRIC : UNIT_SYSTEMS.IMPERIAL
      ),
    distanceUnit: distanceUnit(unitSystem),
    toDistanceUnits: (metres) => distanceInUnits(metres, unitSystem),
    distanceParts: (metres) => distanceParts(metres, unitSystem),
    formatDistance: (metres) => formatDistance(metres, unitSystem),
    formatSpeed: (mph) => formatSpeed(mph, unitSystem),
  }
}

// Metric without a provider, so components render sensibly in isolation
const UnitsContext = createContext(buildValue(UNIT_SYSTEMS.METRIC, () => {}))

/** The user's unit system (metric or imperial) and formatters bound to it */
export const useUnits = () => useContext(UnitsContext)

export const UnitsProvider = ({ children }) => {
  const [unitSystem, setUnitSystemState] = useState(initialUnitSystem)

  const setUnitSystem = useCallback((next) => {
    if (!isUnitSystem(next)) return
    setUnitSystemState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Preference still applies for this session
    }
  }, [])

  const value = useMemo(() => buildValue(unitSystem, setUnitSystem), [unitSystem, setUnitSystem])

  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>
}

UnitsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
